import React from 'react';
import ActivityTimelineChart from '../../../../components/charts/ActivityTimelineChart';
import ActivityFeed from '../../../../components/charts/ActivityFeed';
import UserEngagementChart from '../../../../components/charts/UserEngagementChart';
import { ActivityTabProps } from './types';

const ActivityTab: React.FC<ActivityTabProps> = ({
  analytics,
  auditLogs,
  recentAlerts,
  timelineActivityData,
  activityFeedData,
  userEngagementData,
  formatUptime,
}) => {
  return (
    <div className="space-y-6">
      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider text-left">Total Activities</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{auditLogs.length}</p>
          <p className="text-xs text-slate-500 mt-0.5">System events logged</p>
        </div>
        <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider text-left">Active Users</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{analytics.totalUsers}</p>
          <p className="text-xs text-slate-500 mt-0.5">Last 30 days</p>
        </div>
        <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider text-left">Security Events</p>
          <p className={`text-2xl font-bold mt-1 ${recentAlerts.length > 0 ? 'text-red-600' : 'text-slate-900'}`}>{recentAlerts.length}</p>
          <p className="text-xs text-slate-500 mt-0.5">Monitored alerts</p>
        </div>
        <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider text-left">Pending Tasks</p>
          <p className={`text-2xl font-bold mt-1 ${analytics.pendingTasksCount > 0 ? 'text-amber-600' : 'text-slate-900'}`}>{analytics.pendingTasksCount}</p>
          <p className="text-xs text-slate-500 mt-0.5">Awaiting completion</p>
        </div>
      </div>

      {/* Activity Timeline Chart */}
      {timelineActivityData.length > 0 && (
        <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <h4 className="text-sm font-semibold text-slate-800 mb-4 text-left">Platform Activity Timeline</h4>
          <ActivityTimelineChart
            data={timelineActivityData}
            title=""
            height={250}
          />
        </div>
      )}

      {/* Activity Log + Feed */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* System Activity Log */}
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <h4 className="text-sm font-semibold text-slate-800 text-left">System Activity Log</h4>
          </div>
          <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {auditLogs.length > 0 ? (
              auditLogs.slice(0, 10).map((log: any, index: number) => (
                <div key={index} className="px-5 py-3 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {log.action} <span className="text-slate-400 font-normal">on</span> {log.entity_type}
                    </p>
                    <span className="text-xs text-slate-400 flex-shrink-0 ml-3">
                      {new Date(log.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {(() => {
                      if (typeof log.details === 'string') return log.details;
                      if (log.details?.email && log.details?.loginTime) return `User ${log.details.email} logged in`;
                      if (log.details?.login_method) return `Login via ${log.details.login_method}`;
                      if (log.details?.session_created) return `Session created`;
                      return `By ${log.user_name || 'System'}`;
                    })()}
                  </p>
                </div>
              ))
            ) : (
              <div className="px-5 py-8 text-sm text-slate-400">No recent system activity</div>
            )}
          </div>
        </div>

        {/* Activity Feed */}
        {activityFeedData.length > 0 ? (
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <ActivityFeed
              activities={activityFeedData}
              title="System Activity Feed"
              maxItems={10}
            />
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <h4 className="text-sm font-semibold text-slate-800 mb-3 text-left">System Activity Feed</h4>
            <p className="text-sm text-slate-400">No activity feed data available</p>
          </div>
        )}
      </div>

      {/* Performance Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* User Engagement Chart */}
        {userEngagementData && userEngagementData.labels?.length > 0 && (
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <h4 className="text-sm font-semibold text-slate-800 mb-4 text-left">User Activity Trends</h4>
            <UserEngagementChart
              data={userEngagementData}
              title=""
              height={280}
            />
          </div>
        )}

        {/* Quick Performance Metrics */}
        <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <h4 className="text-sm font-semibold text-slate-800 mb-4 text-left">Performance Summary</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 uppercase tracking-wider text-left">Avg Response</p>
              <p className="text-xl font-bold text-slate-900 mt-1">{analytics.avgResponseTime !== null ? analytics.avgResponseTime + 'ms' : '\u2014'}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 uppercase tracking-wider text-left">Uptime</p>
              <p className="text-xl font-bold text-slate-900 mt-1">{formatUptime(analytics.uptime)}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 uppercase tracking-wider text-left">Total Events</p>
              <p className="text-xl font-bold text-slate-900 mt-1">{analytics.totalAuditLogs}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 uppercase tracking-wider text-left">Active Users</p>
              <p className="text-xl font-bold text-slate-900 mt-1">{analytics.totalUsers}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityTab;
